import type { RowAction } from './types';

export interface BulkActionRunResult {
  succeeded: number;
  failed: Array<{ row: unknown; error: Error }>;
}

export interface BulkActionRunHandlers {
  onProgress?: (done: number, total: number) => void;
  onComplete?: (result: BulkActionRunResult) => void;
}

/**
 * Runs a {@link RowAction} against a selection of rows.
 *
 * - `tier: 'bulk'`  — invokes the action's batched handler once. Errors are
 *   re-thrown to the caller (the action itself owns its toast/error UX).
 * - `tier: 'sequential'` — invokes `onCalled(row)` per row, collecting per-row
 *   errors. Resolves with a {@link BulkActionRunResult} summary.
 *
 * Actions without a `bulk` descriptor cannot be run via this helper.
 */
export async function runBulkAction<TData>(
  action: RowAction<TData>,
  rows: TData[],
  handlers: BulkActionRunHandlers = {},
): Promise<BulkActionRunResult> {
  if (!action.bulk) {
    throw new Error(
      `Action "${labelForRow(action, rows[0])}" is not bulk-capable`,
    );
  }

  if (action.bulk.tier === 'bulk') {
    handlers.onProgress?.(0, rows.length);
    await action.bulk.onCalledBulk(rows);
    handlers.onProgress?.(rows.length, rows.length);
    const result: BulkActionRunResult = { succeeded: rows.length, failed: [] };
    handlers.onComplete?.(result);
    return result;
  }

  // Sequential
  const failed: BulkActionRunResult['failed'] = [];
  let succeeded = 0;
  handlers.onProgress?.(0, rows.length);
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    try {
      await Promise.resolve(action.onCalled(row));
      succeeded += 1;
    } catch (err) {
      failed.push({
        row,
        error: err instanceof Error ? err : new Error(String(err)),
      });
    }
    handlers.onProgress?.(i + 1, rows.length);
  }
  const result: BulkActionRunResult = { succeeded, failed };
  handlers.onComplete?.(result);
  return result;
}

function labelForRow<TData>(
  action: RowAction<TData>,
  row: TData | undefined,
): string {
  if (typeof action.label === 'function') {
    if (row === undefined) return '';
    return action.label(row);
  }
  return action.label;
}

export interface BulkActionCandidate<TData> {
  /** Representative action (bound to the first applicable row). Used for
   * label/icon/variant in the dropdown and confirm modal. */
  action: RowAction<TData>;
  /** Subset of the selection for which the action is applicable
   * (i.e. `disabled(row)` is false). The bulk handler is invoked with this
   * subset only. */
  applicableRows: TData[];
}

/**
 * Picks the bulk-capable actions for a selection. An action is included when
 *  - it declares a `bulk` descriptor, AND
 *  - its `disabled` predicate evaluates to false for AT LEAST ONE selected row.
 *
 * The returned `applicableRows` contains only the rows where the action is
 * enabled. Non-applicable rows are silently filtered out — the bulk handler is
 * invoked against the applicable subset, never the full selection.
 *
 * Actions are deduped across rows by their stable `id`, so the same logical
 * action lines up regardless of dynamic label or icon swaps.
 */
export function pickBulkActions<TData>(
  actions: RowAction<TData>[] | ((data: TData) => RowAction<TData>[]),
  rows: TData[],
): BulkActionCandidate<TData>[] {
  if (rows.length === 0) return [];

  const actionLists = rows.map((row) =>
    typeof actions === 'function' ? actions(row) : actions,
  );

  // Collect unique action ids across all rows, preserving the order of first
  // appearance and remembering a representative match per row.
  const seenIds = new Set<string>();
  const candidates: BulkActionCandidate<TData>[] = [];

  for (let i = 0; i < actionLists.length; i++) {
    for (const action of actionLists[i]) {
      if (!action.bulk) continue;
      if (seenIds.has(action.id)) continue;
      seenIds.add(action.id);

      // For each row, find the matching action and check enabled state.
      const applicableRows: TData[] = [];
      let representative: RowAction<TData> | null = null;
      for (let j = 0; j < actionLists.length; j++) {
        const row = rows[j];
        const match = actionLists[j].find((a) => a.id === action.id);
        if (!match || !match.bulk) continue;
        const disabled =
          typeof match.disabled === 'function'
            ? match.disabled(row)
            : (match.disabled ?? false);
        if (disabled) continue;
        applicableRows.push(row);
        if (!representative) representative = match;
      }

      if (applicableRows.length > 0 && representative) {
        candidates.push({ action: representative, applicableRows });
      }
    }
  }

  return candidates;
}
